import { useEffect, useState } from "react";
import client from "../lib/api";
import { money, fmtDate } from "../lib/helpers";
import { HandCoins, Camera, CalendarCheck, WarningCircle, Clock } from "@phosphor-icons/react";
import { Badge } from "./ui/badge";

export default function PayoutSchedule() {
  const [data, setData] = useState(null);

  useEffect(() => { client.get("/finance/payout-schedule").then((r) => setData(r.data)).catch(() => setData(null)); }, []);

  if (!data) return <p className="text-[#5C5A58]">Loading…</p>;

  const c = data.commission_cycle;
  const s = data.shoot_cycle;
  const commDueLabel = c.overdue
    ? `Overdue by ${Math.abs(c.days_until_due)}d`
    : c.days_until_due === 0 ? "Due today" : `Due in ${c.days_until_due}d`;

  return (
    <div className="space-y-8" data-testid="payout-schedule">
      {/* ---- Commission cycle (monthly) ---- */}
      <section className="space-y-4" data-testid="schedule-commission">
        <div className="surface-kpi overflow-hidden">
          <div className="p-5 bg-[#FAF9F6] border-b border-[#E9DDC6] flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <HandCoins size={22} weight="duotone" className="text-[#8a6d0a]" />
              <div>
                <p className="eyebrow text-[#5C5A58]">Commission cycle · monthly</p>
                <p className="font-display text-2xl leading-tight">{c.period_label}</p>
                <p className="text-xs text-[#5C5A58]">{fmtDate(c.period_start)} – {fmtDate(c.period_end)} · pays out {fmtDate(c.payout_date)}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-display text-3xl tabular-nums text-[#8a6d0a]" data-testid="schedule-commission-total">{money(c.unpaid)}</p>
              <Badge data-testid="schedule-commission-due" className={c.overdue ? "bg-[#E01E37]/10 text-[#E01E37] border-[#E01E37]/30" : "bg-[#B08D57]/15 text-[#8a6d0a] border-[#B08D57]/40"}>
                {c.overdue ? <WarningCircle size={12} weight="fill" className="mr-1" /> : <Clock size={12} weight="fill" className="mr-1" />}{commDueLabel}
              </Badge>
            </div>
          </div>
          <div className="overflow-x-auto">
          <table className="table-editorial text-sm min-w-[600px]">
            <thead>
              <tr className="text-left text-[#5C5A58] border-b border-[#E9DDC6]">
                <th className="p-4 font-medium">Property</th>
                <th className="p-4 font-medium">Earned by</th>
                <th className="p-4 font-medium">Shoot date</th>
                <th className="p-4 font-medium">Commission</th>
                <th className="p-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {c.items.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#5C5A58]">No commissions from {c.period_label} to pay out this cycle.</td></tr>}
              {c.items.map((it, i) => (
                <tr key={it.property_id} className={it.paid ? "row-paid" : (i % 2 ? "bg-[#FBFAF7]" : "bg-white")} data-testid={`schedule-comm-row-${it.property_id}`}>
                  <td className="p-4 font-medium">{it.title}</td>
                  <td className="p-4">{it.photographer}</td>
                  <td className="p-4">{fmtDate(it.shoot_date)}</td>
                  <td className="p-4 font-mono-num font-semibold text-[#8a6d0a]">{money(it.amount)}</td>
                  <td className="p-4">{it.paid
                    ? <Badge className="bg-green-100 text-green-800 border-green-200">Paid</Badge>
                    : <Badge className="bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6]">Owed</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </section>

      {/* ---- Shoot payout cycle (7-day post-deliverables) ---- */}
      <section className="space-y-4" data-testid="schedule-shoots">
        <div className="surface-kpi overflow-hidden">
          <div className="p-5 bg-[#FAF9F6] border-b border-[#E9DDC6] flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Camera size={22} weight="duotone" className="text-[#4A0000]" />
              <div>
                <p className="eyebrow text-[#5C5A58]">Shoot payouts · 7 days after deliverables</p>
                <p className="font-display text-2xl leading-tight">Current cycle</p>
                <p className="text-xs text-[#5C5A58]">Due within 7 days of delivery · overdue at 8 days+</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-display text-3xl tabular-nums text-[#4A0000]" data-testid="schedule-shoots-total">{money(s.total)}</p>
              <div className="flex items-center gap-2 justify-end mt-1">
                {s.overdue_total > 0 && <Badge className="bg-[#E01E37]/10 text-[#E01E37] border-[#E01E37]/30"><WarningCircle size={12} weight="fill" className="mr-1" />{money(s.overdue_total)} overdue</Badge>}
                <Badge className="bg-[#B08D57]/15 text-[#8a6d0a] border-[#B08D57]/40"><Clock size={12} weight="fill" className="mr-1" />{money(s.due_total)} due</Badge>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
          <table className="table-editorial text-sm min-w-[680px]">
            <thead>
              <tr className="text-left text-[#5C5A58] border-b border-[#E9DDC6]">
                <th className="p-4 font-medium">Property</th>
                <th className="p-4 font-medium">Photographer</th>
                <th className="p-4 font-medium">Delivered</th>
                <th className="p-4 font-medium">Pay by</th>
                <th className="p-4 font-medium">Amount</th>
                <th className="p-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {s.items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#5C5A58]">No shoot payouts due this cycle. Payouts appear here once deliverables are submitted.</td></tr>}
              {s.items.map((it, i) => (
                <tr key={it.property_id} className={it.status === "overdue" ? "row-overdue" : (i % 2 ? "bg-[#FBFAF7]" : "bg-white")} data-testid={`schedule-shoot-row-${it.property_id}`}>
                  <td className="p-4 font-medium">{it.title}</td>
                  <td className="p-4">{it.photographer}</td>
                  <td className="p-4">{it.deliverables_at ? fmtDate(it.deliverables_at) : "—"}</td>
                  <td className="p-4">{fmtDate(it.due_date)}</td>
                  <td className="p-4 font-mono-num font-semibold">{money(it.amount)}</td>
                  <td className="p-4">{it.status === "overdue"
                    ? <Badge data-testid={`schedule-shoot-overdue-${it.property_id}`} className="bg-[#E01E37]/10 text-[#E01E37] border-[#E01E37]/30"><WarningCircle size={12} weight="fill" className="mr-1" />Overdue · {it.days_overdue}d</Badge>
                    : <Badge className="bg-[#B08D57]/15 text-[#8a6d0a] border-[#B08D57]/40"><CalendarCheck size={12} weight="fill" className="mr-1" />Due</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </section>
    </div>
  );
}
